<script setup lang="ts">
import { ref, inject, watch } from 'vue'
import { useRoute } from 'vue-router'
import { Menu, X, Gamepad2, ChevronDown, LogIn, UserPlus } from 'lucide-vue-next'

const route = useRoute()
const userSignedIn = inject<boolean>('userSignedIn', false)
const userName = inject<string>('userName', '')
const logoutPath = inject<string>('logoutPath', '')
const editProfilePath = inject<string>('editProfilePath', '')
const loginPath = inject<string>('loginPath', '/users/sign_in')
const signupPath = inject<string>('signupPath', '/users/sign_up')
const csrfToken = document.head.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
const mobileMenuOpen = ref(false)
const userDropdownOpen = ref(false)

function toggleUserDropdown() {
  userDropdownOpen.value = !userDropdownOpen.value
}

function closeUserDropdown() {
  userDropdownOpen.value = false
}

function handleDropdownFocusOut(event: FocusEvent) {
  const container = event.currentTarget as HTMLElement
  if (!container.contains(event.relatedTarget as Node)) {
    userDropdownOpen.value = false
  }
}

const navLinks = [
  { to: '/', label: 'TOP' },
  { to: '/pricing', label: '料金' },
  { to: '/how-to-use', label: '使い方' },
  { to: '/equipment', label: '設備' },
  { to: '/access', label: 'アクセス' },
  { to: '/faq', label: 'FAQ' },
  { to: '/availability', label: '予約状況', external: true },
  { to: '/contact', label: 'お問い合わせ' },
]

function closeMobileMenu() {
  mobileMenuOpen.value = false
}

function isActive(path: string) {
  return route.path === path
}

watch(() => route.path, () => {
  closeMobileMenu()
})
</script>

<template>
  <header class="fixed top-4 left-4 right-4 z-50">
    <div class="max-w-7xl mx-auto bg-white border-4 border-[#5a3a26] shadow-[8px_8px_0px_0px_#3f2618] rounded-sm">
      <div class="px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16 md:h-20">
          <!-- ロゴ -->
          <router-link
            to="/"
            class="flex items-center gap-2 group relative z-10"
            @click="closeMobileMenu"
          >
            <div class="bg-lime-400 absolute -inset-2 skew-x-12 opacity-0 group-hover:opacity-100 transition-opacity -z-10" />
            <!-- Viteビルド時にsrc="/images/..."だとRollupが解決しようとしてエラーになるため:srcを使用 -->
            <img
              :src="'/images/hideout/logo.png'"
              alt="HIDE OUT NAGAI"
              width="139"
              height="48"
              class="h-10 md:h-12 w-auto object-contain transition-transform group-hover:scale-105 group-hover:rotate-2"
            >
          </router-link>

          <!-- デスクトップナビ -->
          <nav class="hidden lg:flex items-center space-x-1">
            <template
              v-for="link in navLinks"
              :key="link.to"
            >
              <a
                v-if="link.external"
                :href="link.to"
                class="px-3 py-2 text-sm font-black tracking-wide transition-all border-2 rounded-sm uppercase whitespace-nowrap text-slate-600 border-transparent hover:border-[#5a3a26] hover:bg-lime-400 hover:shadow-[2px_2px_0px_0px_#3f2618]"
              >
                {{ link.label }}
              </a>
              <router-link
                v-else
                :to="link.to"
                class="px-3 py-2 text-sm font-black tracking-wide transition-all border-2 rounded-sm uppercase whitespace-nowrap"
                :class="isActive(link.to)
                  ? 'text-[#5a3a26] border-[#5a3a26] bg-lime-400 shadow-[2px_2px_0px_0px_#3f2618]'
                  : 'text-slate-600 border-transparent hover:border-[#5a3a26] hover:bg-lime-400 hover:shadow-[2px_2px_0px_0px_#3f2618]'"
              >
                {{ link.label }}
              </router-link>
            </template>

            <!-- ログイン済み: マイ予約 + ユーザードロップダウン -->
            <template v-if="userSignedIn">
              <a
                href="/reservations"
                class="px-3 py-2 text-sm font-black tracking-wide transition-all border-2 rounded-sm uppercase whitespace-nowrap text-slate-600 border-transparent hover:border-[#5a3a26] hover:bg-lime-400 hover:shadow-[2px_2px_0px_0px_#3f2618]"
              >
                マイ予約
              </a>

              <!-- ユーザードロップダウン -->
              <div
                class="relative ml-2"
                @mouseleave="closeUserDropdown"
                @focusout="handleDropdownFocusOut"
              >
                <button
                  class="px-3 py-2 text-sm font-black tracking-wide transition-all border-2 rounded-sm whitespace-nowrap text-slate-600 border-transparent hover:border-[#5a3a26] hover:bg-lime-400 hover:shadow-[2px_2px_0px_0px_#3f2618] flex items-center gap-1"
                  aria-haspopup="true"
                  :aria-expanded="userDropdownOpen"
                  @mouseenter="userDropdownOpen = true"
                  @click="toggleUserDropdown"
                >
                  {{ userName }}様
                  <ChevronDown :size="16" />
                </button>
                <div
                  v-show="userDropdownOpen"
                  class="absolute right-0 top-full pt-2 w-48 z-10"
                >
                  <div class="bg-white border-4 border-[#5a3a26] shadow-[4px_4px_0px_0px_#3f2618] rounded-sm py-1">
                    <a
                      :href="editProfilePath"
                      class="block px-4 py-2 text-sm font-bold text-[#5a3a26] hover:bg-lime-400"
                    >
                      プロフィール編集
                    </a>
                    <form
                      :action="logoutPath"
                      method="post"
                    >
                      <input
                        type="hidden"
                        name="_method"
                        value="delete"
                      >
                      <input
                        type="hidden"
                        name="authenticity_token"
                        :value="csrfToken"
                      >
                      <button
                        type="submit"
                        class="block w-full text-left px-4 py-2 text-sm font-bold text-[#5a3a26] hover:bg-lime-400"
                      >
                        ログアウト
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </template>
            <!-- 未ログイン: ログイン/新規登録 -->
            <template v-else>
              <a
                :href="loginPath"
                class="px-3 py-2 text-sm font-black tracking-wide transition-all border-2 rounded-sm uppercase whitespace-nowrap text-slate-600 border-transparent hover:border-[#5a3a26] hover:bg-lime-400 hover:shadow-[2px_2px_0px_0px_#3f2618] flex items-center gap-1"
              >
                <LogIn :size="16" />
                ログイン
              </a>
              <a
                :href="signupPath"
                class="px-3 py-3 bg-[#84cc16] text-[#3f2618] font-black border-4 border-[#5a3a26] shadow-[4px_4px_0px_0px_#3f2618] active:translate-y-1 active:shadow-none transition-all uppercase tracking-widest rounded-sm text-sm whitespace-nowrap flex items-center gap-1"
              >
                <UserPlus :size="16" />
                新規登録
              </a>
            </template>
          </nav>

          <!-- モバイルメニューボタン -->
          <button
            class="lg:hidden p-2 text-[#5a3a26] hover:bg-lime-400 border-2 border-transparent hover:border-[#5a3a26] transition-all rounded-sm"
            :aria-label="mobileMenuOpen ? 'メニューを閉じる' : 'メニューを開く'"
            :aria-expanded="mobileMenuOpen"
            @click="mobileMenuOpen = !mobileMenuOpen"
          >
            <Menu
              v-if="!mobileMenuOpen"
              :size="28"
              :stroke-width="3"
            />
            <X
              v-else
              :size="28"
              :stroke-width="3"
            />
          </button>
        </div>
      </div>

      <!-- モバイルメニュー -->
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="mobileMenuOpen"
          class="lg:hidden bg-white border-t-4 border-[#5a3a26] overflow-hidden"
        >
          <div class="px-4 py-4 space-y-2">
            <template
              v-for="link in navLinks"
              :key="link.to"
            >
              <a
                v-if="link.external"
                :href="link.to"
                class="block px-4 py-3 border-4 text-base font-black uppercase rounded-sm transition-all text-slate-600 border-transparent hover:bg-slate-100 hover:border-slate-300"
                @click="closeMobileMenu"
              >
                <span class="flex items-center gap-2">
                  {{ link.label }}
                </span>
              </a>
              <router-link
                v-else
                :to="link.to"
                class="block px-4 py-3 border-4 text-base font-black uppercase rounded-sm transition-all"
                :class="isActive(link.to)
                  ? 'bg-lime-400 text-[#5a3a26] border-[#5a3a26] shadow-[4px_4px_0px_0px_#3f2618]'
                  : 'text-slate-600 border-transparent hover:bg-slate-100 hover:border-slate-300'"
                @click="closeMobileMenu"
              >
                <span class="flex items-center gap-2">
                  <Gamepad2
                    v-if="isActive(link.to)"
                    :size="16"
                  />
                  {{ link.label }}
                </span>
              </router-link>
            </template>
            <div class="pt-4 mt-4 border-t-2 border-dashed border-slate-300">
              <template v-if="userSignedIn">
                <a
                  href="/reservations"
                  class="block w-full text-center py-2 px-4 text-sm font-black text-[#5a3a26] hover:text-lime-600 mb-2"
                >
                  マイ予約
                </a>
                <a
                  :href="editProfilePath"
                  class="block w-full text-center py-2 px-4 text-sm font-black text-[#5a3a26] hover:text-lime-600 mb-2"
                >
                  プロフィール編集
                </a>
                <form
                  :action="logoutPath"
                  method="post"
                >
                  <input
                    type="hidden"
                    name="_method"
                    value="delete"
                  >
                  <input
                    type="hidden"
                    name="authenticity_token"
                    :value="csrfToken"
                  >
                  <button
                    type="submit"
                    class="block w-full text-center py-3 px-4 bg-[#84cc16] text-[#3f2618] font-black border-4 border-[#5a3a26] shadow-[4px_4px_0px_0px_#3f2618] active:translate-y-1 active:shadow-none transition-all uppercase tracking-widest rounded-sm"
                  >
                    ログアウト
                  </button>
                </form>
              </template>
              <template v-else>
                <a
                  :href="loginPath"
                  class="block w-full text-center py-2 px-4 text-sm font-black text-[#5a3a26] hover:text-lime-600 mb-2"
                >
                  ログイン
                </a>
                <a
                  :href="signupPath"
                  class="block w-full text-center py-3 px-4 bg-[#84cc16] text-[#3f2618] font-black border-4 border-[#5a3a26] shadow-[4px_4px_0px_0px_#3f2618] active:translate-y-1 active:shadow-none transition-all uppercase tracking-widest rounded-sm"
                >
                  新規登録
                </a>
              </template>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </header>
</template>
